import { useQuery } from '@tanstack/react-query';
import Papa from 'papaparse';
import { api } from './api';
import { i18n } from './i18n';
import { useOrgId } from './org';

export type ImportKind = 'accounts' | 'customers' | 'vendors' | 'items';

export interface ImportField {
  key: string;
  label: string;
  required?: boolean;
  aliases: string[];
  hint?: string;
}

export interface ImportSpec {
  kind: ImportKind;
  title: string;
  fields: ImportField[];
  sample: string[][];
  /** Field key → CSV header, as saved for this client and import type. */
  savedMapping: Record<string, string> | null;
}

export interface ImportRowResult {
  row: number;
  label: string;
  status: 'ok' | 'error';
  errors: Array<{ field: string; message: string }>;
}

export interface ImportResult {
  kind: ImportKind;
  total: number;
  ok: number;
  errors: number;
  rows: ImportRowResult[];
}

/** The import wizards (document 06 G4), in the order QBO lists them. */
export const IMPORT_TYPES: Array<{
  kind: ImportKind;
  title: string;
  description: string;
  list: string;
}> = [
  {
    kind: 'accounts',
    title: 'Chart of accounts',
    description: 'Account numbers, names, types, detail types and sub-accounts.',
    list: '/accounting/chart-of-accounts',
  },
  {
    kind: 'customers',
    title: 'Customers',
    description: 'Names, contact details, addresses, TRNs, terms and default VAT.',
    list: '/sales/customers',
  },
  {
    kind: 'vendors',
    title: 'Vendors',
    description: 'Names, contact details, TRNs, terms, default category and bank details.',
    list: '/expenses/vendors',
  },
  {
    kind: 'items',
    title: 'Products and services',
    description: 'Names, SKUs, sales prices and costs, accounts and tax codes.',
    list: '/sales/products-and-services',
  },
];

/** An import type's title in the reader's language (IMPORT_TYPES keeps the English). */
export function importTypeTitle(kind: ImportKind): string {
  return i18n.t(`settingsPages.data.importTypes.${kind}.title`);
}

/** An import type's description in the reader's language. */
export function importTypeDescription(kind: ImportKind): string {
  return i18n.t(`settingsPages.data.importTypes.${kind}.description`);
}

export const MAX_IMPORT_ROWS = 2000;

export function useImportSpec(kind: ImportKind) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['import-spec', orgId, kind],
    queryFn: () => api<ImportSpec>(`/orgs/${orgId}/imports/${kind}`),
    enabled: Boolean(orgId),
  });
}

export interface ParsedCsv {
  headers: string[];
  rows: string[][];
}

/** Reads a CSV file: first row is the header; blank lines are skipped. */
export function parseCsv(file: File): Promise<ParsedCsv> {
  return new Promise((resolve, reject) => {
    Papa.parse<string[]>(file, {
      skipEmptyLines: 'greedy',
      complete: (res) => {
        const [head, ...rows] = res.data;
        if (!head || !head.some((h) => h.trim())) {
          reject(new Error(i18n.t('settingsPages.data.importWizard.emptyFile')));
          return;
        }
        // a file saved by Excel as "CSV" on an Arabic Windows is Windows-1256, which reads as U+FFFD
        if (res.data.some((r) => r.some((cell) => cell.includes('\uFFFD')))) {
          reject(new Error(i18n.t('settingsPages.data.importWizard.notUtf8')));
          return;
        }
        const headers = head.map((h, i) => h.replace(/^\uFEFF/, '').trim() || `Column ${i + 1}`);
        resolve({ headers, rows });
      },
      error: (err) => reject(err),
    });
  });
}

/** Field key → header: the saved mapping where its headers exist, otherwise known header names. */
export function autoMap(
  fields: ImportField[],
  headers: string[],
  saved: Record<string, string> | null,
): Record<string, string> {
  const out: Record<string, string> = {};
  const taken = new Set<string>();
  const lower = headers.map((h) =>
    h
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/[*:]+$/, ''),
  );
  for (const f of fields) {
    const s = saved?.[f.key];
    if (s && headers.includes(s)) {
      out[f.key] = s;
      taken.add(s);
    }
  }
  for (const f of fields) {
    if (out[f.key]) continue;
    const i = lower.findIndex((h, n) => !taken.has(headers[n]!) && f.aliases.includes(h));
    if (i >= 0) {
      out[f.key] = headers[i]!;
      taken.add(headers[i]!);
    }
  }
  return out;
}

export function toCsv(rows: string[][]): string {
  return Papa.unparse(rows, { newline: '\r\n' });
}

export function downloadCsv(filename: string, rows: string[][]): void {
  // BOM so Excel opens UTF-8 (Arabic names, en dashes) correctly
  const blob = new Blob(['\uFEFF' + toCsv(rows)], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
